import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useGlobal } from '../Context/GlobalContext';
import { useAuth } from '../Auth/AuthContext';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const { apiUrl } = useGlobal();
  const { isLoggedIn } = useAuth();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const userDataRef = useRef(null);
  const isLoggedInRef = useRef(isLoggedIn);
  const initializedRef = useRef(initialized);

  // Rate limiting state
  const [lastApiCall, setLastApiCall] = useState(0);
  const [rateLimitBackoff, setRateLimitBackoff] = useState(0);
  const MIN_API_INTERVAL = 1000; // Minimum 1 second between API calls

  // Circuit breaker state
  const [failureCount, setFailureCount] = useState({});
  const [blockedOperations, setBlockedOperations] = useState(new Set());
  const FAILURE_THRESHOLD = 3; // Number of failures before blocking
  const CIRCUIT_BREAKER_TIMEOUT = 30000; // 30 seconds

  // Debounce utility for API calls with circuit breaker
  const debounceApiCall = useCallback(async (apiFunction, operationName, ...args) => {
    const now = Date.now();

    // Check circuit breaker
    if (blockedOperations.has(operationName)) {
      const lastFailure = failureCount[operationName]?.lastFailure || 0;
      if (now - lastFailure < CIRCUIT_BREAKER_TIMEOUT) {
        throw new Error(`Operation ${operationName} is temporarily blocked due to repeated failures`);
      } else {
        // Reset circuit breaker after timeout
        setBlockedOperations(prev => {
          const next = new Set(prev);
          next.delete(operationName);
          return next;
        });
        setFailureCount(prev => ({ ...prev, [operationName]: { count: 0, lastFailure: 0 } }));
      }
    }

    const timeSinceLastCall = now - lastApiCall;
    const backoffTime = Math.max(rateLimitBackoff - now, 0);

    if (timeSinceLastCall < MIN_API_INTERVAL || backoffTime > 0) {
      const waitTime = Math.max(MIN_API_INTERVAL - timeSinceLastCall, backoffTime);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    setLastApiCall(Date.now());

    try {
      const result = await apiFunction(...args);
      setRateLimitBackoff(0); // Reset backoff on success

      // Reset failure count on success
      if (failureCount[operationName]?.count > 0) {
        setFailureCount(prev => ({ ...prev, [operationName]: { count: 0, lastFailure: 0 } }));
      }

      return result;
    } catch (error) {
      // Track failures for circuit breaker
      const currentFailures = (failureCount[operationName]?.count || 0) + 1;
      setFailureCount(prev => ({
        ...prev,
        [operationName]: { count: currentFailures, lastFailure: Date.now() }
      }));

      if (currentFailures >= FAILURE_THRESHOLD) {
        setBlockedOperations(prev => new Set(prev).add(operationName));
      }

      // Handle 429 rate limiting with exponential backoff
      if (error.message?.includes('429') || error.message?.toLowerCase().includes('too many requests')) {
        const backoffDelay = Math.min(30000, 5000 * Math.pow(2, Math.floor(rateLimitBackoff / 5000))); // Max 30 seconds
        setRateLimitBackoff(Date.now() + backoffDelay);
      }
      throw error;
    }
  }, [lastApiCall, rateLimitBackoff, failureCount, blockedOperations]);

  // Update refs when state changes
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  useEffect(() => {
    initializedRef.current = initialized;
  }, [initialized]);

  // Fetch user data from API
  const fetchUserData = useCallback(async (forceRefresh = false) => {
    // Get fresh token from localStorage in case it changed
    const currentToken = localStorage.getItem('brandvox_token');

    // Don't fetch if not logged in
    if (!isLoggedInRef.current || !currentToken) {
      setUserData(null);
      setInitialized(false);
      return null;
    }

    // Don't fetch if already initialized and not forcing refresh
    if (initializedRef.current && !forceRefresh) {
      return userDataRef.current;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await debounceApiCall(async () => {
        return fetch(`${apiUrl}/api/v1/users/get-user`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json',
          },
        });
      }, 'fetchUserData');

      if (response.ok) {
        const data = await response.json();
        setUserData(data);
        setInitialized(true);
        return data;
      } else {
        if (response.status === 401) {
          // Token is invalid, let AuthContext handle this
          setError('Authentication failed');
          setUserData(null);
          setInitialized(false);
          return null;
        }
        if (response.status === 429) {
          // Rate limited, don't retry immediately
          setError('Too many requests. Please wait a moment.');
          return null;
        }
        throw new Error(`Failed to fetch user data: ${response.statusText}`);
      }
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiUrl, debounceApiCall]);

  // Initialize user data when auth state changes (replaced with enhanced version below)

  // Initialize user data when auth state changes
  useEffect(() => {
    const currentToken = localStorage.getItem('brandvox_token');

    if (currentToken && isLoggedIn && !initialized && !loading) {
      fetchUserData();
    } else if (!currentToken && initialized) {
      setUserData(null);
      setInitialized(false);
      setError(null);
    }
  }, [isLoggedIn, initialized, fetchUserData, loading]);

  // Refresh user data
  const refreshUserData = useCallback(() => {
    return fetchUserData(true);
  }, [fetchUserData]);

  // Clear user data (used on logout)
  const clearUserData = useCallback(() => {
    setUserData(null);
    setInitialized(false);
    setError(null);
    setLoading(false);
  }, []);

  // Get user email
  const getUserEmail = useCallback(() => {
    return userData?.email || localStorage.getItem('user_email') || null;
  }, [userData]);

  // Get user plan
  const getUserPlan = useCallback(() => {
    return userData?.plan || 'Free';
  }, [userData]);

  // Get subscription status
  const getSubscriptionStatus = useCallback(() => {
    return {
      status: userData?.subscription_status || 'inactive',
      type: userData?.subscription_type || 'free',
      endDate: userData?.subscription_end_date,
      isActive: userData?.active_subscription || false
    };
  }, [userData]);

  // Get user limits and usage
  const getUserLimits = useCallback(() => {
    if (!userData) return null;

    return {
      agents: {
        current: userData.workspace_count || 0,
        limit: userData.agent_limit || 1
      },
      characters: {
        current: userData.workspace_character_count || 0,
        limit: userData.character_limit || 5000000
      },
      messages: {
        current: userData.workspace_message_count || 0,
        limit: userData.message_limit || 1000
      },
      crawledUrls: {
        current: userData.workspace_crawl_url_count || 0,
        limit: userData.limits?.workspace_crawl_url_count_limit || 1000
      }
    };
  }, [userData]);

  // Check if user has reached agent limit
  const hasReachedAgentLimit = useCallback(() => {
    const limits = getUserLimits();
    return limits?.agents.current >= limits?.agents.limit;
  }, [getUserLimits]);

  // Cache for processed limits
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Enhanced limit functions (migrated from LimitsContext)
  const getUserLimitsAndUsage = useCallback(() => {
    if (!userData || !userData.limits || !userData.usage) {
      return { limits: null, usage: null };
    }
    return {
      limits: userData.limits,
      usage: userData.usage
    };
  }, [userData]);

  // Check if a user-level limit has been reached
  const hasReachedUserLimit = useCallback((limitType) => {
    const { limits, usage } = getUserLimitsAndUsage();
    if (!limits || !usage) return false;

    switch (limitType) {
      case 'workspace_count':
        return usage.workspace_count >= limits.workspace_count_limit;
      default:
        return false;
    }
  }, [getUserLimitsAndUsage]);

  // Get remaining capacity for a user-level limit
  const getUserRemainingCapacity = useCallback((limitType) => {
    const { limits, usage } = getUserLimitsAndUsage();
    if (!limits || !usage) return 0;

    switch (limitType) {
      case 'workspace_count':
        return Math.max(0, limits.workspace_count_limit - usage.workspace_count);
      case 'total_bv':
        return Math.max(0, limits.total_bv_limit - usage.total_bv_count);
      default:
        return 0;
    }
  }, [getUserLimitsAndUsage]);

  // Get usage percentage for a user-level limit
  const getUserUsagePercentage = useCallback((limitType) => {
    const { limits, usage } = getUserLimitsAndUsage();
    if (!limits || !usage) return 0;

    let used, limit;

    switch (limitType) {
      case 'workspace_count':
        used = usage.workspace_count;
        limit = limits.workspace_count_limit;
        break;
      default:
        return 0;
    }

    if (limit === 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  }, [getUserLimitsAndUsage]);

  // Check if user has access to a user-level feature
  const hasUserFeatureAccess = useCallback((feature) => {
    const { limits } = getUserLimitsAndUsage();
    if (!limits) return false;

    switch (feature) {
      case 'api_access':
        return limits.api_access;
      case 'priority_support':
        return limits.priority_support;
      default:
        return true; // Default to allowed for unknown features
    }
  }, [getUserLimitsAndUsage]);

  // Get user limit info for display
  const getUserLimitInfo = useCallback((limitType) => {
    const { limits, usage } = getUserLimitsAndUsage();
    if (!limits || !usage) {
      return {
        current: 0,
        limit: 0,
        remaining: 0,
        percentage: 0,
        hasReached: false
      };
    }

    return {
      current: usage[limitType.replace('_', '_')],
      limit: limits[`${limitType}_limit`],
      remaining: getUserRemainingCapacity(limitType),
      percentage: getUserUsagePercentage(limitType),
      hasReached: hasReachedUserLimit(limitType)
    };
  }, [getUserLimitsAndUsage, getUserRemainingCapacity, getUserUsagePercentage, hasReachedUserLimit]);

  // Enhanced fetchUserData to also set lastFetchTime
  const enhancedFetchUserData = useCallback(async (forceRefresh = false) => {
    const result = await fetchUserData(forceRefresh);
    if (result) {
      setLastFetchTime(Date.now());
    }
    return result;
  }, [fetchUserData]);

  // Refresh user limits
  const refreshUserLimits = useCallback(() => {
    return enhancedFetchUserData(true);
  }, [enhancedFetchUserData]);

  const value = {
    // Data
    userData,
    loading,
    error,
    initialized,
    lastFetchTime,

    // Actions
    fetchUserData: enhancedFetchUserData,
    refreshUserData,
    refreshUserLimits,
    clearUserData,

    // Getters
    getUserEmail,
    getUserPlan,
    getSubscriptionStatus,
    getUserLimits,
    hasReachedAgentLimit,

    // User-level limit functions (migrated from LimitsContext)
    getUserLimitsAndUsage,
    hasReachedUserLimit,
    getUserRemainingCapacity,
    getUserUsagePercentage,
    hasUserFeatureAccess,
    getUserLimitInfo,

    // Cache info
    isCacheValid: lastFetchTime && (Date.now() - lastFetchTime) < CACHE_DURATION,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;